import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Lock, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCredits } from "@/context/CreditsContext";

const DEFAULT_PACKS = {
  "10": { credits: 10, price_pence: 499 },
  "25": { credits: 25, price_pence: 999 },
  "100": { credits: 100, price_pence: 2999 },
  "300": { credits: 300, price_pence: 8499 },
};
const DEFAULT_CUSTOM = { rate_pence: 30, min: 50, max: 2000 };

export default function CreditsDialog() {
  const { credits, topUpOpen, setTopUpOpen, outOfCredits, setOutOfCredits } = useCredits();
  const [busy, setBusy] = useState(null);
  const [customAmt, setCustomAmt] = useState("");

  const packs = credits?.packs || DEFAULT_PACKS;
  const custom = credits?.custom || DEFAULT_CUSTOM;
  const order = ["10", "25", "100", "300"];

  const close = (v) => {
    setTopUpOpen(v);
    if (!v) setOutOfCredits && setOutOfCredits(null);
  };

  const buy = async (packKey, customCredits) => {
    setBusy(packKey);
    try {
      const body = { pack: packKey, origin: window.location.origin };
      if (packKey === "custom") body.credits = customCredits;
      const { data } = await api.post("/credits/checkout", body);
      window.location.assign(data.checkout_url);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't start checkout — please try again shortly.");
      setBusy(null);
    }
  };

  const customN = parseInt(customAmt, 10) || 0;
  const customValid = customN >= custom.min && customN <= custom.max;
  const customPrice = ((customN * custom.rate_pence) / 100).toFixed(2);

  const isOut = !!outOfCredits;

  return (
    <Dialog open={!!topUpOpen} onOpenChange={close}>
      <DialogContent className="bg-popover max-w-lg" data-testid="credits-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" /> AI credits
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {isOut ? (
            <div
              className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3.5"
              data-testid="out-of-credits-banner"
            >
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-foreground">You're out of AI credits</p>
                <p className="text-muted-foreground mt-0.5">
                  That action needs <span className="font-medium text-foreground">{outOfCredits.needed}</span> credit
                  {outOfCredits.needed !== 1 ? "s" : ""}, but you have{" "}
                  <span className="font-medium text-foreground">{outOfCredits.balance ?? 0}</span>. Top up below to keep
                  using AI. Your free allowance refreshes on the 1st of each month.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Credits power the AI features — Gear Sync, AI Scan, Proofread and the smart supply bar. You get{" "}
              <span className="font-medium text-foreground">25 free</span> every month; top-up packs never expire.
            </p>
          )}

          {credits && !credits.unlimited && (
            <div className="text-sm rounded-xl border border-border bg-secondary/40 px-4 py-3 flex items-center justify-between">
              <span className="text-muted-foreground">Your balance</span>
              <span className="font-semibold" data-testid="credits-dialog-balance">
                {credits.total} credit{credits.total !== 1 ? "s" : ""}
                <span className="text-muted-foreground font-normal ml-1.5">
                  ({credits.free} free · {credits.paid} paid)
                </span>
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {order.map((k) => {
              const p = packs[k];
              if (!p) return null;
              const price = (p.price_pence / 100).toFixed(2);
              const popular = k === "100";
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => buy(k)}
                  disabled={!!busy}
                  data-testid={`credit-pack-${k}`}
                  className={`relative rounded-2xl border p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 ${
                    popular ? "border-primary bg-primary/5" : "border-border bg-card"
                  }`}
                >
                  {popular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                      Best value
                    </span>
                  )}
                  <div className="flex items-center justify-center gap-1 text-2xl font-bold font-display">
                    {busy === k ? <Loader2 className="w-5 h-5 animate-spin" /> : p.credits}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">credits</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">£{price}</div>
                </button>
              );
            })}
          </div>

          <div className="rounded-2xl border border-border bg-secondary/30 p-4" data-testid="credit-pack-custom">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm font-medium">Custom amount</span>
              <span className="text-xs text-muted-foreground">{custom.min}–{custom.max} credits</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number" min={custom.min} max={custom.max} value={customAmt}
                onChange={(e) => setCustomAmt(e.target.value)}
                placeholder={`e.g. 500`}
                data-testid="custom-credits-input"
                className="flex-1 min-w-0 text-sm bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <Button
                onClick={() => buy("custom", customN)}
                disabled={!!busy || !customValid}
                data-testid="buy-custom-credits"
                className="gap-1.5 shrink-0"
              >
                {busy === "custom" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {customValid ? `Buy · £${customPrice}` : "Buy"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Pick exactly what you need — priced per credit. Great for topping up a big premium audiobook.</p>
          </div>

          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3" /> Secure payment handled by Square. We never see your card details.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
